import type { ConversationSummary, Message } from "@teamna/shared";
import { MESSAGE_BODY_MAX } from "@teamna/shared";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import {
  flattenMessages,
  newestCreatedAt,
  useMarkConversationRead,
  useMessages,
  useSendMessage,
} from "@teamna/web-client/hooks/use-messaging.ts";
import { dayKey, formatClock, formatCountdown, formatRelativeDay } from "@teamna/web-client/lib/format.ts";
import { Button } from "./Button.tsx";
import { ChatIcon, MegaphoneIcon, SendIcon } from "./Icons.tsx";
import { EmptyState, ErrorState, SkeletonList } from "./States.tsx";

/**
 * The messaging surface both sides of the app share.
 *
 * A member's thread with the business and the business's thread with that member are the same
 * rows read from the other end, so they are one component with the sides flipped by `mine`. The
 * announcement channel is the same list again with the composer replaced by whatever the screen
 * passes as `footer`: a member gets "replies go to your private chat", the business gets its
 * composer.
 *
 * Polling keeps the first page fresh while the thread is open. Reading marks the thread read as far
 * as the newest message actually on screen, never further, so a message that lands while the
 * screen is open stays unread until it has been seen.
 */

// --- inbox row -------------------------------------------------------------------------

export function ConversationRow({
  row,
  onSelect,
  last = false,
  /** What to call the row. Defaults to the counterpart; the member side overrides with the business. */
  title,
  /** A small line above the title, for the business's name when the title alone would not say which business. */
  eyebrow,
  selected = false,
}: {
  row: ConversationSummary;
  onSelect: () => void;
  last?: boolean;
  title?: string;
  eyebrow?: string;
  /** The row whose thread is open beside the list, at desktop width. */
  selected?: boolean;
}) {
  const unread = row.unreadCount > 0;
  const Icon = row.kind === "announcement" ? MegaphoneIcon : ChatIcon;
  const preview = row.lastMessage
    ? `${row.lastMessage.mine ? "You: " : ""}${row.lastMessage.body}`
    : row.kind === "announcement"
      ? "No announcements yet"
      : "No messages yet";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`${title ?? row.counterpartName}${unread ? `, ${row.unreadCount} unread` : ""}`}
      aria-current={selected ? "true" : undefined}
      className={[
        "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-surface",
        last ? "" : "border-b border-line",
        selected ? "bg-accent-surface/60" : "hover:bg-surface/60",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          row.kind === "announcement" ? "bg-accent-surface text-accent-strong" : "bg-surface text-ink-2",
        ].join(" ")}
      >
        <Icon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        {eyebrow ? (
          <span className="mb-0.5 block truncate text-[11px] font-semibold tracking-[0.04em] text-ink-3 uppercase">
            {eyebrow}
          </span>
        ) : null}
        <span className="flex items-baseline justify-between gap-2">
          <span
            dir="auto"
            className={[
              "truncate text-[15px] text-ink",
              unread ? "font-bold" : "font-semibold",
            ].join(" ")}
          >
            {title ?? row.counterpartName}
          </span>
          {row.lastMessage ? (
            <span className="shrink-0 text-[12px] text-ink-3">
              {formatCountdown(row.lastMessage.createdAt)}
            </span>
          ) : null}
        </span>
        <span
          dir="auto"
          className={[
            "mt-0.5 block truncate text-start text-[13px]",
            unread ? "font-semibold text-ink" : "text-ink-2",
          ].join(" ")}
        >
          {preview}
        </span>
      </span>
      {unread ? (
        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-pill bg-accent px-1.5 text-[11px] font-semibold text-inverse tabular-nums">
          {row.unreadCount > 99 ? "99+" : row.unreadCount}
        </span>
      ) : null}
    </button>
  );
}

// --- the thread ------------------------------------------------------------------------

export function ConversationThread({
  conversation,
  footer,
  emptyTitle,
  emptyMessage,
}: {
  conversation: ConversationSummary;
  /** Below the messages, inside the pinned dock: the composer, or a read-only note or button. */
  footer?: ReactNode;
  emptyTitle: string;
  emptyMessage: string;
}) {
  const messages = useMessages(conversation.id);
  const markRead = useMarkConversationRead();
  const items = useMemo(() => flattenMessages(messages.data?.pages), [messages.data]);
  const newest = newestCreatedAt(items);

  // Mark read as far as the newest message actually rendered. Once per newest value: the same
  // timestamp is never sent twice, and a mark that raced a newer message stays behind it.
  const marked = useRef<string | null>(null);
  useEffect(() => {
    if (!newest || marked.current === newest || markRead.isPending) return;
    marked.current = newest;
    markRead.mutate({ conversationId: conversation.id, through: newest });
    // `markRead` is a stable mutation object; including it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest, conversation.id]);

  // Keep the newest message in view: on first paint, and whenever one arrives.
  const bottom = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [newest]);

  if (messages.isLoading && !messages.data) return <SkeletonList rows={5} />;
  if (messages.error && !messages.data) {
    return <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />;
  }

  return (
    /*
     * Tall enough to push the footer to the bottom of the viewport on a short thread, so the reply
     * box sits where a chat app keeps it rather than floating under the third message. On a long
     * thread the sticky footer takes over and the height no longer matters. The subtraction is the
     * bars at the bottom and top of the window and the screen header above the thread
     * (`--thread-chrome`). `flex-1` lets it fill a flex parent, like the owner's desktop pane.
     *
     * The negative bottom margin cancels the padding the page keeps under this block
     * (`--screen-pad-bottom`). Without it the footer can only stick inside this box, so at the end
     * of the thread it stops that far above the tab bar and a gap shows.
     */
    <div className="-mb-(--screen-pad-bottom) flex min-h-[calc(100dvh-var(--tabbar-height)-var(--safe-top)-var(--safe-bottom)-var(--thread-chrome))] flex-1 flex-col gap-4">
      {messages.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void messages.fetchNextPage()}
            loading={messages.isFetchingNextPage}
          >
            Show older messages
          </Button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          icon={
            conversation.kind === "announcement" ? (
              <MegaphoneIcon size={30} />
            ) : (
              <ChatIcon size={30} />
            )
          }
          title={emptyTitle}
          message={emptyMessage}
        />
      ) : (
        <MessageList items={items} kind={conversation.kind} />
      )}

      <div ref={bottom} aria-hidden="true" />
      {/*
       * The footer's dock, a direct child of the flex column so `sticky` has the whole thread to
       * move in (inside a wrapper it would only have the wrapper's own height and never stick).
       * It sits on the tab bar, or on the bottom edge where the screen has no tab bar. Footers
       * bring their content only; the dock owns the position, the rule and the padding.
       */}
      {footer ? (
        <div
          data-thread-dock=""
          className="sticky bottom-[calc(var(--tabbar-height)+var(--safe-bottom))] z-10 mt-auto -mx-5 border-t border-line bg-paper/95 px-5 py-3 backdrop-blur-md"
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Bubbles, grouped under a day heading. The sender's name is shown on the other side's messages
 * whenever it changes, so a thread with two coaches replying reads naturally on the member's end.
 * Announcements are never "mine" for a member, so they sit on one side with the business's name on
 * each; for the business they flip like any thread.
 */
function MessageList({ items, kind }: { items: Message[]; kind: ConversationSummary["kind"] }) {
  const groups: Array<{ day: string; label: string; items: Message[] }> = [];
  for (const item of items) {
    const day = dayKey(item.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(item);
    else groups.push({ day, label: formatRelativeDay(item.createdAt), items: [item] });
  }

  return (
    <ol className="flex flex-col gap-4" aria-label="Messages">
      {groups.map((group) => (
        <li key={group.day} className="flex flex-col gap-2">
          <p className="py-1 text-center text-[12px] font-semibold text-ink-3">{group.label}</p>
          {group.items.map((item, index) => {
            const previous = group.items[index - 1];
            const showName =
              !item.mine &&
              (kind === "announcement" ||
                previous === undefined ||
                previous.mine ||
                previous.senderName !== item.senderName);
            return <Bubble key={item.id} item={item} showName={showName} />;
          })}
        </li>
      ))}
    </ol>
  );
}

function Bubble({ item, showName }: { item: Message; showName: boolean }) {
  return (
    <div className={["flex flex-col", item.mine ? "items-end" : "items-start"].join(" ")}>
      {showName ? (
        <span dir="auto" className="mb-1 px-1 text-[12px] font-semibold text-ink-2">
          {item.senderName}
        </span>
      ) : null}
      <div
        dir="auto"
        className={[
          "bubble max-w-[85%] rounded-card px-3.5 py-2.5 text-[15px] leading-[1.45] whitespace-pre-wrap break-words",
          // Raised like a button at rest (`.bubble` in styles.css).
          item.mine ? "bubble-mine rounded-br-md" : "bubble-theirs rounded-bl-md",
        ].join(" ")}
      >
        {linkify(item.body)}
      </div>
      <span className="mt-1 px-1 text-[11px] text-ink-3" title={item.createdAt}>
        {formatClock(item.createdAt)}
      </span>
    </div>
  );
}

const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]])/g;

/** Plain text with bare URLs made tappable. Nothing else is interpreted. */
function linkify(text: string): ReactNode {
  const parts = text.split(URL_PATTERN);
  if (parts.length === 1) return text;
  // Split output alternates text, url, text, ... so the position is the identity of a part.
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <a
        // oxlint-disable-next-line react/no-array-index-key
        key={`${index}:${part}`}
        href={part}
        target="_blank"
        rel="noreferrer noopener"
        className="underline underline-offset-2"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

// --- composer --------------------------------------------------------------------------

/**
 * The reply box. Pass it as the `footer` of `ConversationThread`, which pins it on the tab bar. Enter sends, shift+enter breaks a line, which is what
 * every chat app the founders compare this to does. Held to the API's limit so the failure is a
 * counter turning red rather than a 422 after tapping send.
 */
export function Composer({
  conversationId,
  placeholder,
  autoFocus = false,
}: {
  conversationId: string;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const send = useSendMessage(conversationId);
  const [body, setBody] = useState("");

  return (
    <ComposerField
      id={`composer-${conversationId}`}
      label="Message"
      value={body}
      onChange={setBody}
      onSend={(text) => send.mutate(text, { onSuccess: () => setBody("") })}
      placeholder={placeholder}
      max={MESSAGE_BODY_MAX}
      pending={send.isPending}
      error={send.error ? copyForError(send.error).message : null}
      // Passed through to the text box: see `ComposerField`.
      // oxlint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
    />
  );
}

/**
 * The text box and the round Send button of a chat, with no data of its own: the thread composer
 * above and AI Assistant use it. `onSend` gets the trimmed text. `before` sits left of Send (the AI Assistant puts
 * the microphone there).
 */
export function ComposerField({
  id,
  label,
  value,
  onChange,
  onSend,
  placeholder,
  max,
  pending = false,
  error = null,
  autoFocus = false,
  before,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  placeholder: string;
  max: number;
  pending?: boolean;
  error?: string | null;
  autoFocus?: boolean;
  before?: ReactNode;
}) {
  const trimmed = value.trim();
  const overLimit = trimmed.length > max;
  const canSend = trimmed.length > 0 && !overLimit && !pending;

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!canSend) return;
    onSend(trimmed);
  }

  return (
    <form onSubmit={submit}>
      {error ? (
        <p role="alert" className="mb-2 text-[13px] font-semibold text-cancelled">
          {error}
        </p>
      ) : null}
      <div className="flex items-end gap-2">
        <label className="sr-only" htmlFor={id}>
          {label}
        </label>
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          rows={1}
          dir="auto"
          // A thread opened from a "Message" button is opened to type in; the keyboard should
          // already be up. Off by default, on only where the screen asks for it.
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          enterKeyHint="send"
          className={[
            "max-h-32 min-h-11 flex-1 resize-none rounded-[22px] border bg-surface px-4 py-2.5 text-[15px] leading-6 text-ink outline-none placeholder:text-ink-3",
            overLimit ? "border-cancelled" : "border-line focus:border-accent-soft",
          ].join(" ")}
        />
        {before}
        <button
          type="submit"
          disabled={!canSend}
          aria-label="Send"
          className="sk-btn flex size-11 shrink-0 items-center justify-center rounded-full [--sk-p:var(--color-accent)] text-inverse transition-opacity disabled:opacity-40"
        >
          <SendIcon size={20} />
        </button>
      </div>
      {overLimit ? (
        <p className="mt-1.5 text-[12px] font-semibold text-cancelled tabular-nums">
          {trimmed.length} / {max}
        </p>
      ) : null}
    </form>
  );
}
